import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../../components/lds/LdsTable";
import { resolvePath } from "../definition";
import { columnHeader, formatCell, formatListItems, pathLabel } from "../labels";
import type { Catalog, ReportResult, ReportSpec } from "../types";
import { fitWidth, secondaryButton } from "./styles";

type Props = {
  catalog: Catalog;
  definition: ReportSpec;
  result: ReportResult;
  stale?: boolean;
  loading?: boolean;
  onPage: (page: number) => void;
};

/**
 * One line per related row, never wrapped, so line n lines up with line n of the other columns of the same
 * relation (name, age and date of birth of the same child).
 */
export function ListCell({ items }: { items: string[] }): ReactElement {
  if (items.length === 0) return <>—</>;
  return (
    <ul className="divide-y divide-slate-100 dark:divide-white/5">
      {items.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={index} className="h-7 whitespace-nowrap leading-7">{item}</li>
      ))}
    </ul>
  );
}

/** The executed report, one page at a time. */
export default function ResultTable({ catalog, definition, result, stale, loading, onPage }: Props): ReactElement {
  const { t } = useTranslation();
  const first = result.total === 0 ? 0 : result.page * result.size + 1;
  const last = Math.min(result.total, (result.page + 1) * result.size);
  const lastPage = Math.max(0, Math.ceil(result.total / result.size) - 1);

  const columns = result.columns.map((column) => {
    const resolved = column.path ? resolvePath(catalog, definition.source, column.path) : null;
    const spec = definition.columns.find((item) => item.path === column.path);
    return {
      column,
      field: resolved?.field ?? null,
      header: column.label || (spec ? columnHeader(t, catalog, definition.source, spec)
        : pathLabel(t, resolved, column.path ?? column.key)),
    };
  });

  return (
    <div className="space-y-3">
      {result.notices.length > 0 && (
        <p role="status" className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-100">
          {t("reports.builder.columns_unavailable", {
            count: result.notices.length,
            defaultValue: "{{count}} column(s) are not available to you and were left out.",
          })}
        </p>
      )}
      {result.restrictedToOwnClasses && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t("reports.builder.restricted_note", { defaultValue: "Shows only what falls within your own classes." })}
        </p>
      )}

      <LdsTableCard className={`${fitWidth}${stale ? " opacity-50" : ""}`} aria-busy={loading}>
        {stale && (
          <p className="px-5 pt-4 text-sm font-bold text-slate-600 dark:text-slate-300">
            {t("reports.builder.stale", { defaultValue: "The layout has changed – run the report again." })}
          </p>
        )}
        <LdsTableScroll className="p-3">
          <LdsTable>
            <LdsTableHead>
              <LdsTableRow>
                {columns.map(({ column, header }) => (
                  <LdsTableHeadCell key={column.key}>{header}</LdsTableHeadCell>
                ))}
              </LdsTableRow>
            </LdsTableHead>
            <LdsTableBody>
              {result.rows.length === 0 ? (
                <LdsTableRow>
                  <LdsTableCell colSpan={Math.max(1, columns.length)} className="py-8 text-center text-slate-500">
                    {t("reports.builder.no_results", { defaultValue: "Nothing meets the conditions." })}
                  </LdsTableCell>
                </LdsTableRow>
              ) : (
                result.rows.map((row, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <LdsTableRow key={index}>
                    {columns.map(({ column, field }) => (
                      <LdsTableCell key={column.key} className="align-top">
                        {column.display === "LIST"
                          ? <ListCell items={formatListItems(t, column.type, field, row[column.key],
                            Number(row[`${column.key}__more`] ?? 0))} />
                          : formatCell(t, column.type, column.display, field, row[column.key])}
                      </LdsTableCell>
                    ))}
                  </LdsTableRow>
                ))
              )}
            </LdsTableBody>
          </LdsTable>
        </LdsTableScroll>
      </LdsTableCard>

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600 dark:text-slate-300">
        <span>
          {t("reports.builder.range_of", { first, last, total: result.total, defaultValue: "{{first}}–{{last}} of {{total}}" })}
        </span>
        <div className="flex gap-2">
          <button type="button" className={secondaryButton} disabled={loading || result.page === 0}
                  onClick={() => onPage(result.page - 1)}>
            {t("reports.builder.previous", { defaultValue: "Previous" })}
          </button>
          <button type="button" className={secondaryButton} disabled={loading || result.page >= lastPage}
                  onClick={() => onPage(result.page + 1)}>
            {t("reports.builder.next", { defaultValue: "Next" })}
          </button>
        </div>
      </div>
    </div>
  );
}
